import { Button, Modal, Paragraph, Spinner, TextArea, TextField } from '@purpur/library';
import { ChangeEvent, useRef, useState, useEffect } from 'react';
import './AccessibilityModal.scss';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';

import PurpurIcon from 'components/purpur/Icon/Icon';
import { submitAccessibilityForm } from 'services/contact-us.service';

const AccessibilityModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  const [email, setEmail] = useState('');
  const [issueDescription, setIssueDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isError, setIsError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);

  const recaptchaRef = useRef<ReCAPTCHA>();
  const reCaptchaPublicKey = process.env.REACT_APP_GOOGLE_RE_CAPTCHA_PUBLIC_KEY_CONTACT_FORM;
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen && firstFieldRef.current) {
      firstFieldRef.current.focus();
    }
  }, [isOpen]);

  const resetFetchStates = () => {
    setIsLoading(false);
    setIsSubmitted(false);
    setIsError(false);
    recaptchaRef.current?.reset();
  };

  const closeModal = () => {
    setIsOpen(false);
    setEmail('');
    setIssueDescription('');
    setIsLoading(false);
    setIsSubmitted(false);
    recaptchaRef.current?.reset();
  };

  const submitForm = async () => {
    if (!issueDescription.trim()) {
      setDescriptionError(true);
      return;
    }
    if (isLoading || (!issueDescription && !issueDescription.trim())) {
      return;
    }
    try {
      const token = await recaptchaRef.current?.executeAsync();
      setIsLoading(true);
      if (token) {
        const response = await submitAccessibilityForm({
          email: email ?? 'n/a',
          token,
          message: issueDescription.trim(),
        });
        if (response.statusCode === 200) {
          setIsLoading(false);
          setIsSubmitted(true);
          setIsError(false);
        }
        if (response.statusCode !== 200) {
          setIsLoading(false);
          setIsSubmitted(false);
          setIsError(true);
        }
      }
    } catch (e) {
      setIsError(true);
      console.error(e);
    }
  };

  let modalContent;
  if (isError) {
    modalContent = (
      <div className="accessibility-modal__error" role="alert" aria-live="assertive">
        <PurpurIcon iconName="error" size="md" />
        <Paragraph className="accessibility-modal__error-text">
          {t('accessibility_modal.error')}
        </Paragraph>
      </div>
    );
  } else if (isSubmitted) {
    modalContent = (
      <div className="accessibility-modal__thankyou" aria-live="polite">
        <Paragraph>{t('accessibility_modal.submit_preamble')}</Paragraph>
        <div>
          <Paragraph>Best regards,</Paragraph>
          <Paragraph>Teliacompany.com team</Paragraph>
        </div>
      </div>
    );
  } else {
    modalContent = (
      <>
        <Paragraph id="accessibility-modal-desc">{t('accessibility_modal.preamble')}</Paragraph>
        <div className="accessibility-modal__form-fields">
          <TextField
            label={t('accessibility_modal.email_label')}
            placeholder={t('accessibility_modal.email_placeholder')}
            helperText={t('accessibility_modal.email_helper_text')}
            type="email"
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            aria-label={t('accessibility_modal.email_label')}
            aria-required="false"
            autoFocus
          />
          <TextArea
            label={t('accessibility_modal.description_label')}
            id="Accessibility-text-area"
            placeholder={t('accessibility_modal.description_placeholder')}
            required
            value={issueDescription}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setIssueDescription(e.target.value)}
            minLength={1}
            maxLength={1000}
            errorText={
              descriptionError && !issueDescription.trim()
                ? t('accessibility_modal.description_error')
                : ''
            }
            aria-label={t('accessibility_modal.description_label')}
            aria-required="true"
            aria-describedby="accessibility-modal-desc"
          />
        </div>
      </>
    );
  }

  return (
    <>
      <Modal open={isOpen} onOpenChange={setIsOpen}>
        <Modal.Trigger>
          <Button variant="primary" className="accessibility-modal__button" aria-haspopup="dialog">
            {t('accessibility_modal.trigger_text')}
          </Button>
        </Modal.Trigger>
        <Modal.Content
          title={
            isSubmitted ? t('accessibility_modal.title_submitted') : t('accessibility_modal.title')
          }
          stickyButtons
          closeButtonAriaLabel="Close"
          showCloseButton
          zIndex={2}
          actions={
            <>
              {!isSubmitted && !isError && (
                <Button
                  disabled={isLoading}
                  aria-disabled={isLoading}
                  aria-label="Submit accessibility issue"
                  variant="primary"
                  onClick={() => submitForm()}
                >
                  {t('actions.submit')}
                </Button>
              )}
              {!isSubmitted && isError && (
                <Button
                  disabled={isLoading}
                  aria-disabled={isLoading}
                  aria-label="Reset form after error"
                  variant="primary"
                  onClick={() => resetFetchStates()}
                >
                  {t('actions.reset')}
                </Button>
              )}
              <Button variant="secondary" onClick={() => closeModal()} aria-label="Close modal">
                {t('actions.close')}
              </Button>
            </>
          }
        >
          <div className="accessibility-modal__container">
            {isLoading && !isError && (
              <div className="accessibility-modal__spinner-overlay" aria-live="polite">
                <Spinner />
              </div>
            )}
            <div
              className="accessibility-modal__content"
              style={{
                filter: isLoading ? 'blur(4px)' : undefined,
                pointerEvents: isLoading ? 'none' : undefined,
                transition: 'filter 0.2s',
              }}
            >
              {modalContent}
            </div>
          </div>
        </Modal.Content>
      </Modal>
      <ReCAPTCHA
        /* @ts-ignore */
        ref={recaptchaRef}
        sitekey={reCaptchaPublicKey!}
        size="invisible"
      />
    </>
  );
};

export default AccessibilityModal;
